// web_components/WebComponent_Base.ts

import { attachServerStylesheets } from '../utils/dom';
import { WebComponent_Utils } from './WebComponent_Utils';
import { observePosition } from '../utils/observePosition';
import { addSkeletonLoading, removeSkeletonLoading } from '../utils/SkeletonLoading';
import { applyContentSquareOverride } from '../utils/contentSquare';

export abstract class WebComponent_Base extends HTMLElement {
	private templateReference: HTMLElement | null = null;

	protected constructor() {
		super();
		this.attachShadow({ mode: 'open' });
	}

	connectedCallback(): void {
		const componentName: string = this.localName;

		try {
			const styles: HTMLStyleElement[] = WebComponent_Utils.createStyleTags(componentName);
			styles.forEach((style: HTMLStyleElement) => {
				this.shadowRoot!.appendChild(style);
			});
		} catch (error: unknown) {
			console.warn(
				`[${this.componentName}] Failed to inject styles for ${componentName}:`,
				error,
			);
		}

		addSkeletonLoading(this);

		const initialise = (): void => {
			attachServerStylesheets(this.shadowRoot!)
				.then(() => {
					this.onConnected();
					this.getComponentData();
					this.initialize();
					applyContentSquareOverride(this.shadowRoot);

					requestAnimationFrame(() => {
						WebComponent_Utils.whenElementDefined(this, () => {
							this.afterInitialize();

							if (this.templateReference) {
								observePosition(this.templateReference);
							}
						});
					});
				})
				.catch((error: unknown) => {
					console.error(`[${this.componentName}] Error constructing component:`, error);
				});
		};

		if (document.readyState === 'loading') {
			window.addEventListener('DOMContentLoaded', initialise, { once: true });
		} else {
			initialise();
		}
	}

	disconnectedCallback(): void {
		this.onDisconnected(); // Optional lifecycle hook for child components
	}

	/**
	 * Gets the name of the component.
	 *
	 * This getter should be overridden in child classes to return a string
	 * representing the name of the specific component. By default, it returns
	 * `'UnnamedComponent'` if not overridden.
	 *
	 * @returns {string} The name of the component.
	 */
	protected get componentName(): string {
		return 'UnnamedComponent';
	}

	/**
	 * Method for child components to retrieve and process their specific data attributes.
	 * Child components should override this method to handle their own data attributes.
	 * data-css, data-store, data-type, etc.
	 */
	protected getComponentData(): void {}

	/**
	 * Optional lifecycle hook called when the component is connected.
	 * Child components can override this method to perform additional actions.
	 */
	protected onConnected(): void {}

	/**
	 * Optional lifecycle hook called when the component is disconnected.
	 * Child components can override this method to perform cleanup.
	 */
	protected onDisconnected(): void {}

	/**
	 * Initialization method called after stylesheets are attached.
	 * Handles rendering and attaching event listeners.
	 */
	protected initialize(): void {
		this.templateReference = this.render();
		this.conditionalVisibility();
		this.attachListeners();
	}

	/**
	 * Abstract method to render the component's HTML structure.
	 * Must be implemented by child components.
	 */
	protected abstract render(): HTMLElement | null;

	/**
	 * Apply conditional visibility to the rendered component, hiding empty
	 * tags, hiding images without src or alt attributes etc.
	 */
	protected conditionalVisibility(): void {}

	/**
	 * Optional method to attach event listeners.
	 */
	protected attachListeners(): void {}

	/**
	 * Optional method for child components to perform additional setup after initialization.
	 */
	protected afterInitialize(): void {
		removeSkeletonLoading(this);
	}
}
